import { Head, router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Trash2, UserPlus, Users, ArrowLeft, Info } from 'lucide-react'
import { useState } from 'react'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface Dependent {
  id: number
  name: string
  email: string
  phone: string
  created_at: string
}

interface User {
  id: number
  name: string
  email: string
  phone: string
  role: string
  active_subscriptions_count: number
}

interface AvailableStudent {
  value: number
  label: string
}

interface Props {
  user: User
  dependents: Dependent[]
  available_students: AvailableStudent[]
}

export default function Index({ user, dependents, available_students }: Props) {
  const [selectedStudent, setSelectedStudent] = useState<string>('')

  const handleAddDependent = () => {
    if (!selectedStudent) return

    router.post(`/users/${user.id}/dependents`, {
      dependent_id: selectedStudent,
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setSelectedStudent('')
      },
    })
  }

  const handleRemoveDependent = (dependentId: number, dependentName: string) => {
    if (confirm(`Tem certeza que deseja remover ${dependentName} como dependente?`)) {
      router.delete(`/users/${user.id}/dependents/${dependentId}`, {
        preserveScroll: true,
      })
    }
  }

  return (
    <DashboardLayout>
      <Head title={`Dependentes de ${user.name}`} />

      <div className="container mx-auto py-8 px-4 max-w-5xl">
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => router.visit('/students')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Gerenciar Dependentes</h1>
            <p className="text-muted-foreground mt-1">
              Dependentes de <strong>{user.name}</strong>
            </p>
          </div>
        </div>

        {/* Informações do Usuário */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Informações do Titular</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Nome</p>
                <p className="font-medium">{user.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{user.email}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pacotes Ativos</p>
                <p className="font-medium text-green-600">
                  {user.active_subscriptions_count} {user.active_subscriptions_count === 1 ? 'pacote' : 'pacotes'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alerta informativo */}
        <Alert className="mb-6">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>Como funciona:</strong> Ao adicionar um aluno como dependente, ele poderá usar os pacotes de aulas de <strong>{user.name}</strong>. 
            As aulas agendadas para dependentes descontarão automaticamente do pacote do titular.
          </AlertDescription>
        </Alert>

        {/* Adicionar Dependente */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Adicionar Dependente
            </CardTitle>
            <CardDescription>
              Selecione um aluno que poderá usar os pacotes de {user.name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="student">Selecione o aluno</Label>
                <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                  <SelectTrigger id="student">
                    <SelectValue placeholder="Escolha um aluno..." />
                  </SelectTrigger>
                  <SelectContent>
                    {available_students.map((student) => (
                      <SelectItem key={student.value} value={student.value.toString()}>
                        {student.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleAddDependent}
                  disabled={!selectedStudent}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Adicionar
                </Button>
              </div>
            </div>
            {available_students.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Não há alunos disponíveis para adicionar como dependentes.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Lista de Dependentes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Dependentes ({dependents.length})
            </CardTitle>
            <CardDescription>
              Alunos que podem usar os pacotes de {user.name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {dependents.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">Nenhum dependente cadastrado</p>
                <p className="text-sm">Use o formulário acima para adicionar alunos como dependentes.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {dependents.map((dependent) => (
                  <div
                    key={dependent.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-lg">{dependent.name}</p>
                      <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-4 mt-1">
                        <p className="text-sm text-muted-foreground">
                          📧 {dependent.email}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          📱 {dependent.phone}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Adicionado em: {dependent.created_at}
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRemoveDependent(dependent.id, dependent.name)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remover
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
